import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getGrowthToday,
  markTouchReplied,
  previewAdhocEmail,
  sendTodayNote,
  skipTodayPerson,
  type CampaignContent,
  type GrowthTouch,
  type TodayItem,
  type TodayReport
} from '../lib/adminClient'
import { useAuth } from '../lib/auth'
import { dateTime, num } from '../lib/format'
import { AccountLink, DrillValue } from '../components/Drill'
import { LoadError } from '../components/LoadError'
import { GoalTile } from '../components/GoalTile'

// Who to write to today, and why (GET /admin/growth/today, server:
// adminGrowth.ts). Everyone here was put here by their own records: a checkout
// they did not finish, credits they used up, a trial about to end. Each line
// carries a draft that names those records; nothing goes out until an admin
// edits it if they like and presses Send, and a note goes to that one person
// only. People with an account are here. Outreach to a target market is a
// different job and lives entirely on the Prospects page; the two are not
// mixed (owner, 2026-11-02).


type State = { s: 'loading' } | { s: 'ready'; r: TodayReport } | { s: 'error'; error: string }

/** The draft as one block of text: paragraphs separated by a blank line. */
const toText = (paragraphs: string[]): string => paragraphs.join('\n\n')
const toParagraphs = (text: string): string[] => text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)

function Person({ item, canSend, canSendAtAll, onDone }: { item: TodayItem; canSend: boolean; canSendAtAll: boolean; onDone: (msg: string) => void }): JSX.Element {
  const [subject, setSubject] = useState(item.draft.subject)
  const [body, setBody] = useState(toText(item.draft.paragraphs))
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [writing, setWriting] = useState(false)
  const content = (): CampaignContent => ({ subject: subject.trim(), paragraphs: toParagraphs(body), button: null })
  const id = item.accountId

  async function showPreview(): Promise<void> {
    setError(null)
    const res = await previewAdhocEmail({ purpose: 'announcement', content: content(), accountId: id })
    if (res.ok) setPreview(res.data.html)
    else setError(res.error)
  }
  async function send(): Promise<void> {
    setBusy(true)
    setError(null)
    const res = await sendTodayNote(id, item.reason, content())
    setBusy(false)
    setConfirming(false)
    if (res.ok) onDone(`Note to ${item.email} queued. It shows under “Notes sent” below.`)
    else setError(res.error)
  }
  async function skip(days: number): Promise<void> {
    setBusy(true)
    const res = await skipTodayPerson(id, item.reason, days)
    setBusy(false)
    if (res.ok) onDone(`${item.email} skipped for ${days} days.`)
    else setError(res.error)
  }

  const blocked = item.unsubscribed ? 'They unsubscribed from our emails, so no note can be sent.' : !canSendAtAll ? 'The server cannot send announcements (no unsubscribe secret is set).' : !canSend ? 'Only an owner or admin can send.' : null
  return (
    <div className="glass-card rounded-xl p-5" data-testid={`today-item-${id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium">
            <AccountLink id={id}>{item.name ? `${item.name} · ${item.email}` : item.email}</AccountLink>
          </div>
          <div className="text-xs text-accent mt-0.5">{item.reasonLabel}</div>
          <ul className="text-xs text-slate-400 mt-1.5 space-y-0.5 list-disc pl-4">
            {item.evidence.map((e) => (
              <li key={e}>{e}</li>
            ))}
            <li>{item.lastEmailedAt ? `Last email from us: ${dateTime(item.lastEmailedAt)}` : 'No email from us on record'}</li>
          </ul>
        </div>
        <div className="flex gap-2">
          {!writing && (
            <button className="btn-primary text-xs" onClick={() => setWriting(true)} data-testid={`today-write-${id}`}>
              Edit and send…
            </button>
          )}
          <button className="btn-ghost text-xs" disabled={busy} onClick={() => void skip(30)} data-testid={`today-skip-${id}`}>
            Skip 30 days
          </button>
        </div>
      </div>
      {!writing && (
        <div className="text-xs text-slate-500 mt-2">
          Draft: “{subject}”{item.unsubscribed ? ' · unsubscribed, cannot be sent' : ''}
          {error && <span className="block text-red-300 mt-1" data-testid={`today-error-${id}`}>{error}</span>}
        </div>
      )}

      {writing && (
      <>
      <div className="mt-4 space-y-2">
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-accent"
          aria-label="Subject"
          data-testid={`today-subject-${id}`}
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={Math.min(10, body.split('\n').length + 1)}
          className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm leading-relaxed focus:outline-none focus:border-accent"
          aria-label="Message"
          data-testid={`today-body-${id}`}
        />
        <div className="text-[11px] text-slate-500">
          A blank line starts a new paragraph. {'{first_name}'} becomes their first name, or “there”. The email adds the unsubscribe link and our sender details.
          Replies go to the connect@ inbox.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button className="btn-ghost text-xs" onClick={() => void showPreview()} data-testid={`today-preview-${id}`}>
          Preview
        </button>
        {blocked ? (
          <span className="text-xs text-amber-200" data-testid={`today-blocked-${id}`}>{blocked}</span>
        ) : confirming ? (
          <>
            <span className="text-xs text-slate-300">Send this to {item.email} now?</span>
            <button className="btn-primary text-xs" disabled={busy} onClick={() => void send()} data-testid={`today-confirm-${id}`}>
              {busy ? 'Sending…' : 'Yes, send'}
            </button>
            <button className="btn-ghost text-xs" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button className="btn-primary text-xs" onClick={() => setConfirming(true)} data-testid={`today-send-${id}`}>
            Send…
          </button>
        )}
        {error && <span className="w-full text-xs text-red-300" data-testid={`today-error-${id}`}>{error}</span>}
      </div>
      {preview && (
        <div className="mt-3">
          <iframe title={`Preview for ${item.email}`} sandbox="" srcDoc={preview} className="w-full h-[420px] bg-white rounded" data-testid={`today-preview-frame-${id}`} />
          <button className="btn-ghost text-xs mt-2" onClick={() => setPreview(null)}>Close preview</button>
        </div>
      )}
      </>
      )}
    </div>
  )
}

const DELIVERY: Record<string, string> = {
  pending: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  not_sent: 'Not sent',
  skipped: 'Not sent (unsubscribed)',
  cancelled: 'Cancelled',
  unknown: 'Unknown (server stopped mid-send)'
}

function afterText(t: GrowthTouch): string {
  const a = t.after
  const parts: string[] = []
  if (a.payingSinceTouch) parts.push('Subscribed after the note')
  else if (a.paying) parts.push('Paying')
  if (a.checkout) parts.push('Started a checkout')
  if (a.signedIn) parts.push('Used Plexii again')
  return parts.length ? parts.join(' · ') : 'Nothing yet'
}

function Recent({ touches, onChange }: { touches: GrowthTouch[]; onChange: () => void }): JSX.Element {
  const [error, setError] = useState<string | null>(null)
  async function toggle(t: GrowthTouch): Promise<void> {
    const res = await markTouchReplied(t.id, t.repliedAt === null)
    if (res.ok) onChange()
    else setError(res.error)
  }
  if (!touches.length) return <div className="glass-card rounded-xl p-5 text-sm text-slate-500">Nothing sent or skipped from here in the last 30 days.</div>
  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <table className="admin-table" data-testid="today-recent">
        <thead>
          <tr>
            <th>When</th>
            <th>Person</th>
            <th>Why they were on the list</th>
            <th>The email</th>
            <th>Since then</th>
            <th>Replied</th>
          </tr>
        </thead>
        <tbody>
          {touches.map((t) => (
            <tr key={t.id} data-testid={`today-touch-${t.id}`}>
              <td className="whitespace-nowrap text-xs">{dateTime(t.createdAt)}</td>
              <td className="text-sm"><AccountLink id={t.accountId}>{t.email ?? t.accountId}</AccountLink></td>
              <td className="text-xs text-slate-300">{t.reasonLabel}</td>
              <td className="text-xs">
                {t.kind === 'skip' ? (
                  <span className="text-slate-500">Skipped until {t.skipUntil ? dateTime(t.skipUntil).slice(0, 10) : '—'}</span>
                ) : t.campaignId ? (
                  <DrillValue to={{ set: 'campaignRecipients', filters: { campaign: t.campaignId, account: t.accountId } }} label={`The note to ${t.email ?? t.accountId}`}>
                    {DELIVERY[t.delivery ?? ''] ?? t.delivery ?? 'Not recorded'}
                  </DrillValue>
                ) : (
                  'Not recorded'
                )}
                {t.subject && <div className="text-slate-500 truncate max-w-[14rem]">{t.subject}</div>}
              </td>
              <td className="text-xs text-slate-300" data-testid={`today-after-${t.id}`}>{t.kind === 'skip' ? '' : afterText(t)}</td>
              <td>
                {t.kind === 'email' && (
                  <button className="btn-ghost text-xs" onClick={() => void toggle(t)} data-testid={`today-replied-${t.id}`}>
                    {t.repliedAt ? `Yes, ${dateTime(t.repliedAt).slice(0, 10)}` : 'Mark replied'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error && <div className="text-xs text-red-300 p-3">{error}</div>}
      <div className="text-[11px] text-slate-500 p-3 border-t border-white/5">
        Replies land in the connect@ inbox, which this server cannot read: mark them here when you see one. “Since then” is read from their account’s records.
      </div>
    </div>
  )
}

export default function Today(): JSX.Element {
  const session = useAuth()
  const [state, setState] = useState<State>({ s: 'loading' })
  const [notice, setNotice] = useState<string | null>(null)
  const load = useCallback(async () => {
    const res = await getGrowthToday()
    setState(res.ok ? { s: 'ready', r: res.data.today } : { s: 'error', error: res.error })
  }, [])
  useEffect(() => void load(), [load])
  const canSend = session?.role === 'owner' || session?.role === 'admin'

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Growth</div>
        <h1 className="text-2xl font-semibold mt-1">Today</h1>
        <div className="text-sm text-slate-400 mt-1">Who to write to today, and why. Nothing is sent until you press Send.</div>
      </div>
      <GoalTile />
      {state.s === 'loading' && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading…</div>}
      {state.s === 'error' && <LoadError what="today’s list" endpoint="GET /admin/growth/today" error={state.error} testId="today-error" />}
      {state.s === 'ready' && (
        <>
          {notice && (
            <div className="glass-card rounded-xl p-3 mb-4 text-sm text-emerald-200" data-testid="today-notice">
              {notice}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="today-reasons">
            {state.r.byReason.map((b) => (
              <span key={b.reason} className="glass-card rounded-full px-3 py-1 text-xs text-slate-300">
                {b.label}:{' '}
                {b.count ? (
                  <DrillValue to={b.ref} label={`Today: ${b.label}`} expect={{ key: 'count', value: b.count }} testId={`today-reason-${b.reason}`}>
                    {num(b.count)}
                  </DrillValue>
                ) : (
                  <span className="text-slate-500">0</span>
                )}
              </span>
            ))}
          </div>

          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-3">People with an account ({num(state.r.items.length)})</div>
          {state.r.items.length === 0 ? (
            <div className="glass-card rounded-xl p-5 text-sm text-slate-500 mb-8" data-testid="today-empty">
              Nobody’s records put them on today’s list. People written to in the last 14 days, and anyone you skipped, are left out.
            </div>
          ) : (
            <div className="space-y-4 mb-8">
              {state.r.items.map((i) => (
                <Person
                  key={`${i.accountId}|${i.reason}`}
                  item={i}
                  canSend={canSend}
                  canSendAtAll={state.r.canSend}
                  onDone={(m) => {
                    setNotice(m)
                    void load()
                  }}
                />
              ))}
            </div>
          )}

          {state.r.newLeads > 0 && (
            <div className="glass-card rounded-xl p-4 mb-8 text-sm text-slate-300 flex items-center justify-between gap-3" data-testid="today-leads">
              <span>
                {num(state.r.newLeads)} website form {state.r.newLeads === 1 ? 'submission has' : 'submissions have'} not been picked up yet.
              </span>
              <Link className="btn-ghost text-xs" to="/leads">
                Open Leads
              </Link>
            </div>
          )}

          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-3">Notes sent and people skipped (last 30 days)</div>
          <Recent touches={state.r.recent} onChange={() => void load()} />
        </>
      )}
    </div>
  )
}
